/**
 * BreakGlassGate — het scherm tussen een beheerder en de leerdata van één kind.
 *
 * Toont niet de gegevens maar de VRAAG: waarom wil je erbij? Pas na een reden
 * van betekenis gaat het venster open, en dat wordt vastgelegd.
 *
 * DRIE KEUZES
 *
 * 1. De reden is een vrij tekstveld en geen keuzelijst. Een lijst nodigt uit
 *    tot de eerste optie aanklikken; typen dwingt tot formuleren, en dat is
 *    precies wat een auditlog bruikbaar maakt.
 *
 * 2. Het venster is kort (zie `BREAK_GLASS_MINUTES`) en niet een hele dag. Break
 *    glass is bedoeld voor "ik zoek nu iets uit", niet voor "ik wil er de rest
 *    van de week bij kunnen". Het getal staat bewust niet in de knoptekst
 *    gehardcodeerd: dan kan de knop iets anders beloven dan er wordt aangevraagd.
 *
 * 3. Er staat expliciet dat het wordt vastgelegd. Niet als dreigement maar
 *    omdat een auditspoor waar de betrokkene niets van weet, geen rem is.
 */

import React, { useState } from 'react';
import { ShieldAlert, Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { BREAK_GLASS_MINUTES } from '../../../data/supabase/dataAccess';

/**
 * Minimale lengte van een reden. Geen willekeurig getal maar een ondergrens die
 * "ok", "test" en "check" uitsluit — de invullingen die een logboek onbruikbaar
 * maken zonder dat iemand een regel overtreedt.
 */
const MIN_REASON_LENGTH = 10;
interface BreakGlassGateProps {
  learnerName: string;
  /** Actief venster? Dan tonen we de balk met de "sluiten"-knop. */
  isOpen: boolean;
  expiresAt?: number;
  /**
   * Gezet wanneer een venster hier niet te openen is (bijvoorbeeld: dit account
   * bestaat alleen op dit apparaat). Dan tonen we de uitleg in plaats van een
   * knop die onvermijdelijk faalt.
   */
  unavailableReason?: string;
  onOpen: (reason: string) => Promise<{
    ok: boolean;
    message?: string;
  }>;
  onClose: () => void;
}
export function BreakGlassGate({
  learnerName,
  isOpen,
  expiresAt,
  unavailableReason,
  onOpen,
  onClose
}: BreakGlassGateProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (isOpen) {
    const minutesLeft = expiresAt ? Math.max(0, Math.round((expiresAt - Date.now()) / 60000)) : null;
    return <div className="flex flex-wrap items-center justify-between gap-3 p-3 mb-4 rounded-xl border border-amber-200 bg-amber-50" data-id="element-8139" data-mp="2ly88ne2de9p8">
        {/*
           Benoem de OMVANG, niet alleen dat het open staat. "Toegang geopend"
           laat een beheerder volledige rechten verwachten; wat hij krijgt is
           inzage in de voortgang. Zonder die regel leest het uitblijven van meer
           velden als een storing in plaats van als de grens van dit venster.
          */}
        <p className="text-sm text-amber-900" data-id="element-8140" data-mp="1y7b970rrh4uv">
          <span className="font-bold" data-id="element-8141" data-mp="1y95s69jh2mwb">Inzage in de voortgang geopend.</span> Dit
          venster laat leerresultaten zien; persoonsgegevens wijzigen hoort bij
          de ouder of de school. De inzage is vastgelegd
          {minutesLeft !== null && ` en sluit over ${minutesLeft} min`}.
        </p>
        <Button variant="secondary" onClick={onClose} data-id="element-8142" data-mp="1o45tlx8iqddf">
          <X className="w-4 h-4 mr-2" data-id="element-8143" data-mp="1t27fepxbwqsl" />
          Toegang sluiten
        </Button>
      </div>;
  }
  // Niet van toepassing is iets anders dan geweigerd. Een knop aanbieden die
  // altijd op dezelfde fout uitkomt, laat een beheerder aan het mechanisme
  // twijfelen in plaats van aan de herkomst van dit account.
  if (unavailableReason) {
    return <div className="p-5 mb-4 rounded-xl border border-slate-200 bg-slate-50" data-id="element-8144" data-mp="26sau8vtvpi80">
        <h4 className="flex items-center gap-2 font-bold text-slate-900 mb-1" data-id="element-8145" data-mp="2bujri4dsb6fx">
          <ShieldAlert className="w-4 h-4 text-slate-500" data-id="element-8146" data-mp="2gsl8mjrngvor" />
          Toegangsvenster niet mogelijk
        </h4>
        <p className="text-sm text-slate-600" data-id="element-8147" data-mp="hg90pqdmq27i">{unavailableReason}</p>
      </div>;
  }
  const reasonTooShort = reason.trim().length < MIN_REASON_LENGTH;
  const handleOpen = async () => {
    setBusy(true);
    setError(null);
    const res = await onOpen(reason);
    setBusy(false);
    if (!res.ok) setError(res.message ?? 'Openen is niet gelukt.');else setReason('');
  };
  return <div className="p-5 mb-4 rounded-xl border border-slate-200 bg-slate-50" data-id="element-8148" data-mp="2lwm1un28ucrl">
      <h4 className="flex items-center gap-2 font-bold text-slate-900 mb-1" data-id="element-8149" data-mp="2gwzn296roogo">
        <ShieldAlert className="w-4 h-4 text-slate-500" data-id="element-8150" data-mp="2guwo2znmzkzt" />
        Leergegevens zijn afgeschermd
      </h4>
      <p className="text-sm text-slate-600 mb-4" data-id="element-8151" data-mp="1t52csmiyu4ho">
        De voortgang en resultaten van {learnerName} zijn niet standaard
        zichtbaar. Geef aan waarom je ze nodig hebt; dat wordt samen met je
        naam en het tijdstip vastgelegd.
      </p>

      <label htmlFor="breakglass-reason" className="block text-sm font-semibold text-slate-700 mb-1" data-id="element-8152" data-mp="1y5gm07wazkha">
        
        Reden voor inzage
      </label>
      <textarea id="breakglass-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Bijv. Ouder meldt dat toetsresultaten ontbreken (ticket 412)" aria-describedby="breakglass-reason-hint" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" data-id="element-8153" data-mp="1aekocu561v24" />

      {/*
         De ondergrens hoort ZICHTBAAR te zijn. Hij bestond al als
         `disabled`-voorwaarde, maar zonder tekst: een beheerder typte "test",
         zag een knop die niet reageerde en had geen enkele aanwijzing waarom.
         Een grijze knop zonder uitleg leest als een kapot mechanisme, en dat is
         precies het verkeerde signaal bij de laag die inzage moet afdwingen.
          De hint blijft staan (ook als de eis vervuld is) en verandert alleen van
         toon. Verdwijnende hulptekst laat een beheerder twijfelen of hij het nu
         wel goed heeft.
        */}
      <p id="breakglass-reason-hint" className={`mt-1.5 text-xs ${reasonTooShort ? 'text-slate-500' : 'font-medium text-emerald-700'}`} data-id="element-8154" data-mp="2fesmtwwa8p2k">
        {reasonTooShort ? `Nog ${MIN_REASON_LENGTH - reason.trim().length} tekens te gaan — beschrijf de aanleiding, niet alleen "controle". Een reden die niemand later kan navertellen, maakt het logboek waardeloos.` : 'Deze reden komt in het logboek te staan, samen met je naam en het tijdstip.'}
      </p>

      {error && <p role="alert" className="mt-2 text-sm font-semibold text-rose-600" data-id="element-8155" data-mp="1ri5w9wyf5t6p">
          {error}
        </p>}

      <div className="mt-3" data-id="element-8156" data-mp="1hmb2q6snqdp5">
        <Button onClick={handleOpen} disabled={busy || reasonTooShort} data-id="element-8157" data-mp="1hmkunzx9iuxh">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-8158" data-mp="1cmq7a6cp4nh6" />}
          Toegang openen ({BREAK_GLASS_MINUTES} min)
        </Button>
      </div>
    </div>;
}